import { nativeAppHandler } from './native-app-handler.mjs';

/**
 * Default key used to store the initial path of the app in the session storage
 * @constant
 */
const DEFAULT_INITIAL_PATH_STORAGE_KEY = 'guru-initial-path';
/**
 * Returns the current path and search parameters of the URL
 * @returns {string} - the current path and search parameters
 * @example
 * // returns '/path?query=value'
 * getCurrentPathWithSearch()
 */
function getCurrentPathWithSearch() {
    return window.location.pathname + window.location.search;
}
/**
 * Check if AndroidHandler or flutter_inappwebview is available.
 * @returns {boolean} - true if AndroidHandler or flutter_inappwebview is available, false otherwise
 */
const isAppHandlerAvailable = () => {
    return !!window.AndroidHandler || !!window.flutter_inappwebview;
};
/**
 * Store initial path to storage when AndroidHandler is available.
 * Put this function on pages/_app.tsx in Next.js
 * or Layout component in other framework.
 * @param {string} [customKey=DEFAULT_INITIAL_PATH_STORAGE_KEY] - custom key for storing the key
 *
 * @example
 * // stores '/path' in the session storage with the key 'DEFAULT_INITIAL_PATH_STORAGE_KEY'
 * storeAppInitialPath()
 *
 * // stores '/path' in the session storage with the key 'custom-key'
 * storeAppInitialPath('custom-key')
 */
function storeAppInitialPath(customKey) {
    const pathToStore = window.location.pathname;
    const key = customKey ?? DEFAULT_INITIAL_PATH_STORAGE_KEY;
    const currentPath = window.sessionStorage.getItem(key);
    if (!currentPath && isAppHandlerAvailable()) {
        window.sessionStorage.setItem(key, pathToStore);
    }
}
/**
 * Back to app from webview using jsbridge AndroidHandler.
 * @param {function} backFn fallback back function for non-webview
 * @param {string} customKey custom storage key
 *
 * @example
 * ```typescript
 * backToApp(() => window.history.back())
 * ```
 */
function backToApp(backFn, customKey) {
    const key = customKey ?? DEFAULT_INITIAL_PATH_STORAGE_KEY;
    const initialPath = window.sessionStorage.getItem(key);
    const currentPath = window.location.pathname;
    if (initialPath === currentPath && isAppHandlerAvailable()) {
        const appHandler = nativeAppHandler();
        window.sessionStorage.removeItem(key);
        appHandler.backToApp();
    }
    else {
        backFn();
    }
}

export { DEFAULT_INITIAL_PATH_STORAGE_KEY, backToApp, getCurrentPathWithSearch, storeAppInitialPath };
